import type {
  ResearchStateSection,
  ResearchStateView,
  UploadDocumentSelection,
} from "../../../web/src/api/types";

export type CadenceMode = "demo" | "live";
export type CadenceTab = "results" | "data" | "map";
export type ArtifactOpenMode = "tab" | "beside";
export type CadenceTurnStatus =
  | "pending"
  | "running"
  | "interrupted_recoverable"
  | "result_unknown"
  | "completed"
  | "error"
  | "cancelled";
export type CadenceEvidenceStatus = "verified" | "unverified" | "quarantined";
export type CadenceEvidenceOrigin = "local" | "network" | "demo" | "unknown";
export type AgentStatus =
  "working" | "waiting" | "paused" | "complete" | "error" | "unknown";
export type CollaborationAgentState =
  "idle" | "working" | "waiting" | "communicating" | "completed" | "failed";
export type CadenceStreamState =
  "idle" | "connecting" | "open" | "reconnecting" | "error";

/** The project Composer's default request state and timeline-driven continuation state. */
export type CadenceComposerIntent =
  | { kind: "project_investigation" }
  | { kind: "research_plan" }
  | { kind: "controlled_command"; command: string }
  | {
      kind: "continue_research";
      parentRequestId: string;
      targetAgentId: string;
    };

/** Public ProjectTurn metadata intentionally omits a controlled command body. */
export type CadenceProjectTurnIntent =
  | { kind: "project_investigation" }
  | { kind: "research_plan" }
  | { kind: "controlled_command" }
  | {
      kind: "continue_research";
      parentRequestId: string;
      targetAgentId: string;
    };

export type CadenceComposerCapabilityKind =
  | "project_investigation"
  | "research_plan"
  | "controlled_command";

/** Server-supplied actions for `/`; unavailable actions remain informative. */
export interface CadenceComposerCapability {
  id: string;
  kind: CadenceComposerCapabilityKind;
  label: string;
  description: string;
  available: boolean;
  /** A controlled Runtime command needs an explicit `@Agent` target. */
  requiresAgentSelection?: boolean;
  permissionRequirement?: string;
  sideEffect?: string;
}

/** Server-resolved references selected with `@` (Artifacts) or `#` (ProjectTurns). */
export type CadenceComposerReference =
  | {
      id: string;
      kind: "artifact";
      artifactId: string;
      label: string;
      detail?: string;
    }
  | {
      id: string;
      kind: "project_turn";
      requestId: string;
      label: string;
      detail?: string;
    };

export interface CadenceProject {
  id: string;
  sessionId?: string;
  workspaceId?: string;
  workspacePath?: string;
  name: string;
  subtitle: string;
  question?: string;
  status: "running" | "paused" | "complete" | "archived";
  owner?: string | null;
  ownerUserId?: string | null;
  resumable?: boolean;
  updatedAt?: string;
}

export interface CadenceAgent {
  id: string;
  threadId?: string;
  name: string;
  role: string;
  status: AgentStatus;
  btwAvailable?: boolean;
  collaborationState?: CollaborationAgentState;
  summary: string;
  finding?: string;
  progress?: number;
  avatar?: string;
  accent?: string;
  artifactCount?: number;
  recentArtifact?: string;
  collaboratingWith?: string[];
  communications?: string[];
}

export type CadenceCollaborationKind = "interaction" | "waiting";

export interface CadenceCollaborationInteraction {
  id: string;
  kind: CadenceCollaborationKind;
  protocolSource: "native" | "raw_response_item";
  sourceAgentId: string;
  targetAgentIds: string[];
  preview?: string;
  messageId?: string;
  needReply?: boolean;
  seq: number;
  updatedSeq: number;
  completed: boolean;
}

/**
 * A provider-created child thread projected for the current Project view.
 *
 * This is deliberately not an Organization roster member: `record` is an
 * event envelope, while `childThreadId` is the stable VCode/Codex identity
 * used to deduplicate a delegated Runtime Agent across its lifecycle.
 */
export interface CadenceDelegatedAgent {
  /** Stable UI key: project + parent thread + child thread. */
  id: string;
  projectId: string;
  parentThreadId: string;
  childThreadId: string;
  /** The spawn tool call is route provenance, not the child identity. */
  parentCallId?: string;
  /** Present only after an exact thread-ID match with the Organization roster. */
  rosterAgentId?: string;
  title: string;
  subtitle?: string;
  status: AgentStatus;
  createdSeq: number;
  updatedSeq: number;
}

export interface CadenceCollaborationState {
  projectId: string;
  interactions: CadenceCollaborationInteraction[];
  delegatedAgents?: CadenceDelegatedAgent[];
}

export type CadenceExternalEvidenceRetrievalStatus =
  | "candidate-sources"
  | "insufficient-or-conflicting"
  | "provider-unavailable"
  | "fetched-candidate"
  | "source-fetch-unavailable"
  | "evidence-not-found";

export interface CadenceExternalEvidenceCandidateSource {
  title: string;
  url: string;
  snippet: string;
  evidence_id?: string;
  citation_key?: string;
  provider?: string;
  source_type?: string;
  stable_ids?: Record<string, string>;
  published_date?: string;
  evidence_status?: string;
  needs_fetch?: boolean;
  locator?: Record<string, unknown>;
  limitations?: string[];
  score?: number;
}

/**
 * Allowlisted, Execution-only outcome for a governed external retrieval.
 * This is not an Artifact, evidence verification, or Project Research State.
 */
export interface CadenceExternalEvidenceExecutionOutcome {
  /** Search results carry this; bounded fetch results may omit it. */
  disclaimer?: string;
  schema_version?: string;
  execution_id?: string;
  decision_context?: string;
  status: CadenceExternalEvidenceRetrievalStatus;
  sources?: CadenceExternalEvidenceCandidateSource[];
  source?: CadenceExternalEvidenceCandidateSource;
  detail?: string;
  credits_used?: number;
  provider_call_made?: boolean;
  quality_gate?: Record<string, unknown>;
  evidence_ledger?: Record<string, unknown>;
  next_action?: string;
  /** Server-issued, authenticated, short-lived receipt URL; never constructed client-side. */
  receipt_download_url?: string;
}

export interface CadencePublicAgentEvent extends Record<string, unknown> {
  type: string;
  structured_content?: CadenceExternalEvidenceExecutionOutcome;
  structuredContent?: CadenceExternalEvidenceExecutionOutcome;
  result?: unknown;
}

export interface CadenceAgentEvent {
  key: string;
  seq: number;
  tsMs?: number;
  eventId?: string;
  source?: string;
  sourceIndex?: number;
  sourceWatermark?: string | number;
  organizationSessionId?: string;
  projectId?: string;
  agentId: string;
  threadId: string;
  submissionId: string;
  event: CadencePublicAgentEvent;
}

export interface CadenceTurn {
  id: string;
  requestId?: string;
  projectId?: string;
  kind?: string;
  input: string;
  output: string;
  status: CadenceTurnStatus;
  createdAt?: string | number;
  completedAt?: string | number;
  error?: string;
  attachments: CadenceAttachment[];
  routedAgentIds?: string[];
  activityIds?: string[];
  resultSummary?: string;
  artifactIds?: string[];
  evidenceIds?: string[];
  /** Immutable, server-resolved scope for an Artifact inquiry. */
  artifactContext?: Record<string, unknown>;
  /** Public, persisted Project action returned by the server. */
  intent?: CadenceProjectTurnIntent;
  /** Planner-selected Lead that owns PASEO-style follow-up input. */
  primaryAgentId?: string;
  /** Public continuity projection; follow-ups do not create sibling turns. */
  steeredInputs?: Array<{
    followUpId: string;
    input: string;
    createdAt: string | number;
    /** The Runtime bridge confirms delivery; this is not inferred from turn status. */
    deliveryStatus: "queued" | "delivered" | "rejected";
    deliveryError?: string;
  }>;
  optimistic?: boolean;
  /** The client is reconciling a lost submission response with ProjectTurn. */
  submissionUncertain?: boolean;
}

export interface CadenceEvidence {
  id: string;
  title: string;
  status: CadenceEvidenceStatus;
  origin: CadenceEvidenceOrigin;
  confidence?: string | number;
  confidenceExplanation?: string;
  summary?: string;
  warnings: string[];
  notes: string[];
  source?: string;
}

export interface CadencePermissionRequest {
  requestId: string;
  target: string;
  reason: string;
  status?: string;
}

export interface ArtifactVersion {
  id: string;
  label: string;
  createdAt: string;
  note?: string;
}

export interface ArtifactProvenance {
  source: string;
  recordedAt?: string;
  evidenceId?: string;
}

export interface ScientificPoint {
  id: string;
  dose_nm: number;
  response_percent: number;
  replicate: string;
  series_id?: string;
  fitted_value?: number;
  residual?: number;
  excluded?: boolean;
  anomaly_fixture?: boolean;
}

export interface CurveFitDescriptor {
  bottom: number;
  top: number;
  ic50_nm: number;
  hill_slope: number;
}

export interface CurveConcentrationMapping {
  name: "log10_concentration";
  input_field: "dose_nm";
  input_unit: string;
  output_unit: string;
  formula: "log10(dose_nm)";
}

export interface CurveResponseConfidenceBand {
  method: "scipy_curve_fit_covariance_delta_method";
  confidence_level: number;
  parameter_order: ["bottom", "top", "ic50_nm", "hill_slope"];
  samples: Array<{
    dose_nm: number;
    lower_response_percent: number;
    upper_response_percent: number;
  }>;
}

export interface CurveScientificArtifactDocument {
  [key: string]: unknown;
  renderer: "curve";
  revision: number;
  title?: string;
  description?: string;
  data: {
    points: ScientificPoint[];
  };
  source: {
    kind: "csv" | "xlsx";
    path: string;
    sha256: string;
    dose_unit: string;
    concentration: {
      field: "dose_nm";
      unit: string;
    };
    x_mapping: CurveConcentrationMapping;
    reference: string;
  };
  state: {
    visible_replicates: string[];
    excluded_point_ids: string[];
  };
  derived: {
    parameters: CurveFitDescriptor;
    confidence_interval_95?: { ic50_nm?: [number, number] };
    goodness_of_fit?: { r_squared?: number; rmse?: number };
    response_confidence_band_95: CurveResponseConfidenceBand;
    residuals?: Array<{
      point_id: string;
      dose_nm: number;
      response_percent: number;
      fitted_value: number;
      residual: number;
      excluded: boolean;
    }>;
    curve_geometry: Array<{ dose_nm: number; response_percent: number }>;
  };
  versions: Array<{
    id: string;
    label: string;
    created_at: string;
    note?: string;
  }>;
  provenance: Array<{
    source: string;
    recorded_at: string;
    evidence_id?: string;
  }>;
}

export interface FlowEvent {
  id: string;
  values: Record<string, number>;
}

export interface FlowGate {
  id: string;
  name: string;
  parent_population: string;
  transform?: string;
  geometry: {
    kind: "polygon";
    vertices: Array<[number, number]>;
  };
  axes: [string, string];
}

export interface FlowChannel {
  id: string;
  label: string;
}

export interface FlowScientificArtifactDocument {
  [key: string]: unknown;
  renderer: "flow";
  revision: number;
  title?: string;
  description?: string;
  data: {
    event_count: number;
    channels: FlowChannel[];
    sampled_events: FlowEvent[];
  };
  source: {
    kind: "fcs";
    path: string;
    sha256: string;
    sample_id: string;
    reference: string;
    compensation: {
      reference: "FCS";
      semantics: string;
    };
    transform: {
      name: "linear" | "asinh";
      parameters: Record<string, number>;
    };
    event_authority: {
      kind: "full_fcs_events";
      semantics: string;
    };
  };
  state: {
    x_channel: string;
    y_channel: string;
    transform: string;
    transform_spec: {
      name: "linear" | "asinh";
      parameters: Record<string, number>;
    };
    gates: FlowGate[];
    selected_gate_id?: string;
  };
  derived: {
    population_authority: "full_fcs_events";
    populations?: Record<
      string,
      {
        event_count: number;
        percentage: number;
        statistics: Record<
          string,
          { median: number; q05: number; q95: number }
        >;
      }
    >;
  };
  versions: Array<{
    id: string;
    label: string;
    created_at: string;
    note?: string;
  }>;
  provenance: Array<{
    source: string;
    recorded_at: string;
    evidence_id?: string;
  }>;
}

export type ScientificArtifactDocument =
  CurveScientificArtifactDocument | FlowScientificArtifactDocument;

/**
 * A complete scientific payload without a canonical revision. It is allowed
 * for local inspection and selection only; it can never back a CAS update.
 */
export interface LocalCurveScientificArtifactDocument {
  [key: string]: unknown;
  renderer: "curve";
  revision?: undefined;
  title?: string;
  description?: string;
  data: CurveScientificArtifactDocument["data"];
  source: CurveScientificArtifactDocument["source"];
  state: CurveScientificArtifactDocument["state"];
  derived: CurveScientificArtifactDocument["derived"];
  versions: CurveScientificArtifactDocument["versions"];
  provenance: CurveScientificArtifactDocument["provenance"];
}

export interface LocalFlowScientificArtifactDocument {
  [key: string]: unknown;
  renderer: "flow";
  revision?: undefined;
  title?: string;
  description?: string;
  data: FlowScientificArtifactDocument["data"];
  source: FlowScientificArtifactDocument["source"];
  state: FlowScientificArtifactDocument["state"];
  derived: FlowScientificArtifactDocument["derived"];
  versions: FlowScientificArtifactDocument["versions"];
  provenance: FlowScientificArtifactDocument["provenance"];
}

export type LocalScientificArtifactDocument =
  | LocalCurveScientificArtifactDocument
  | LocalFlowScientificArtifactDocument;

export type ScientificArtifactInspectionDocument =
  | ScientificArtifactDocument
  | LocalScientificArtifactDocument;

export type ScientificSelectionGeometry =
  | {
      type: "box";
      xMin: number;
      xMax: number;
      yMin: number;
      yMax: number;
    }
  | {
      type: "polygon";
      points: Array<{ x: number; y: number }>;
    };

export type ScientificUiValue =
  | string
  | number
  | boolean
  | null
  | ScientificUiValue[]
  | { [key: string]: ScientificUiValue };

export interface ScientificUiSelectionFidelity {
  version: 1;
  descriptorRevision?: number;
  source?: {
    dataRef?: string;
    artifactRef?: string;
    artifactRevision?: number;
  };
  selectionId?: string;
  rowIds?: string[];
  bindingArtifactRefs?: string[];
  catalogState?: {
    controlValues: Record<string, ScientificUiValue>;
    selectedRowIds: Record<string, string[]>;
  };
}

export interface ScientificUiSelection {
  renderer: "scientific-ui" | "custom-app";
  artifactId: string;
  ids: string[];
  componentId: string;
  interaction: "select" | "change" | "activate";
  value?: ScientificUiValue;
  actionId?: string;
  fidelity?: ScientificUiSelectionFidelity;
  geometry?: ScientificSelectionGeometry;
  gateId?: string;
  channels?: { x: string; y: string };
  points?: Array<Record<string, unknown>>;
  gate?: FlowGate;
  axes?: [string, string];
  transform?: string;
}

export interface ScientificSelectionBase {
  renderer: "curve" | "flow";
  artifactId: string;
  ids: string[];
  geometry?: ScientificSelectionGeometry;
  gateId?: string;
  channels?: { x: string; y: string };
  points?: Array<Record<string, unknown>>;
  gate?: FlowGate;
  axes?: [string, string];
  transform?: string;
}

export type CadenceStructureFormat = "pdb" | "cif";

export interface CadenceStableResidue {
  /** Coordinate-model number assigned by the source PDB/mmCIF. */
  model_num: number;
  chain_id: string;
  auth_seq_id: number | string;
  label_seq_id?: number | string;
  insertion_code?: string;
  residue_name?: string;
  stable: true;
  canonical_key: string;
}

export interface StructureScientificSelection {
  renderer: "structure";
  artifactId: string;
  artifactRef?: string;
  ids: string[];
  format: CadenceStructureFormat;
  /** Server-calculated digest of the exact coordinate Artifact bytes. */
  coordinateSha256?: string;
  sourceRevision?: number;
  descriptorRevision?: number;
  residues: CadenceStableResidue[];
  legacyResiduePositions: number[];
}

export interface TextScientificSelection {
  renderer: "report";
  artifactId: string;
  quote: string;
  prefix: string;
  suffix: string;
}

export interface ReportTextReplacementProposal {
  version: 1;
  operation: "report.replace_selection";
  artifact_id: string;
  base_content_sha256: string;
  selection: TextScientificSelection;
  replacement: string;
}

export type ScientificSelection =
  | ScientificSelectionBase
  | ScientificUiSelection
  | StructureScientificSelection
  | TextScientificSelection;

const SCIENTIFIC_UI_SELECTION_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

/**
 * The only scientific-ui shape admitted to an Owner-scoped Artifact inquiry.
 * Catalog controls and arbitrary renderer event values remain local state.
 */
export function scientificUiArtifactInquirySelection(
  selection: ScientificSelection | undefined,
  descriptorRevision?: number,
): ScientificUiSelection | undefined {
  if (
    selection?.renderer !== "scientific-ui" ||
    selection.interaction !== "select" ||
    !SCIENTIFIC_UI_SELECTION_ID.test(selection.componentId) ||
    !selection.artifactId.trim()
  ) {
    return undefined;
  }
  const fidelity = selection.fidelity;
  const source = fidelity?.source;
  const revision = fidelity?.descriptorRevision;
  const rowIds = fidelity?.rowIds;
  if (
    fidelity?.version !== 1 ||
    typeof revision !== "number" ||
    !Number.isSafeInteger(revision) ||
    revision < 1 ||
    (descriptorRevision !== undefined && revision !== descriptorRevision) ||
    !source ||
    !validScientificUiArtifactReference(source.dataRef) ||
    !validScientificUiArtifactReference(source.artifactRef) ||
    !Array.isArray(rowIds) ||
    rowIds.length === 0 ||
    rowIds.length > 250 ||
    rowIds.some((id) => !SCIENTIFIC_UI_SELECTION_ID.test(id)) ||
    new Set(rowIds).size !== rowIds.length ||
    selection.ids.length !== rowIds.length + 1 ||
    selection.ids[0] !== selection.componentId ||
    selection.ids.slice(1).some((id, index) => id !== rowIds[index])
  ) {
    return undefined;
  }
  return {
    renderer: "scientific-ui",
    artifactId: selection.artifactId,
    ids: [selection.componentId, ...rowIds],
    componentId: selection.componentId,
    interaction: "select",
    fidelity: {
      version: 1,
      descriptorRevision: revision,
      source: {
        dataRef: source.dataRef,
        artifactRef: source.artifactRef,
      },
      rowIds: [...rowIds],
    },
  };
}

function validScientificUiArtifactReference(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 2_048 &&
    value.trim() === value &&
    !/[\u0000-\u001f\u007f]/.test(value)
  );
}

export interface ScientificOperationResult {
  version: 1;
  operation: "curve.exclude_selected_refit" | "flow.adjust_selected_gate";
  renderer: "curve" | "flow";
  base_revision: number;
  agent_id?: string;
  thread_id?: string;
  selection: ScientificSelectionBase;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  preview_geometry?: ScientificSelectionGeometry;
}

export type ScientificUiPrimitive =
  | "Row"
  | "Column"
  | "Tabs"
  | "Text"
  | "Markdown"
  | "Metric"
  | "DataTable"
  | "Slider"
  | "RangeSlider"
  | "Select"
  | "CheckBox"
  | "Button"
  | "VegaLiteChart"
  | "PlotlyVolcanoChart"
  | "ArtifactViewer";

export interface ScientificUiNodeBase {
  id: string;
  type: ScientificUiPrimitive;
}

export interface ScientificUiContainerNode extends ScientificUiNodeBase {
  type: "Row" | "Column";
  children: ScientificUiNode[];
  gap?: number;
}

export interface ScientificUiTabsNode extends ScientificUiNodeBase {
  type: "Tabs";
  tabs: Array<{ id: string; label: string; content: ScientificUiNode }>;
  defaultTabId?: string;
}

export interface ScientificUiTextNode extends ScientificUiNodeBase {
  type: "Text";
  text: string;
  tone?: "default" | "muted" | "accent";
}

export interface ScientificUiMarkdownNode extends ScientificUiNodeBase {
  type: "Markdown";
  content: string;
}

export interface ScientificUiMetricNode extends ScientificUiNodeBase {
  type: "Metric";
  label: string;
  value: string | number;
  unit?: string;
  detail?: string;
}

export interface ScientificUiRow {
  id: string;
  values: Record<string, ScientificUiValue>;
}

export interface ScientificUiColumn {
  id: string;
  label: string;
}

export type ScientificUiFilterOperator =
  "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "between" | "in";

export interface ScientificUiFilterBinding {
  controlId: string;
  column: string;
  op: ScientificUiFilterOperator;
}

export interface ScientificUiDataTableNode extends ScientificUiNodeBase {
  type: "DataTable";
  columns: ScientificUiColumn[];
  rows?: ScientificUiRow[];
  binding?: string;
  dataRef?: string;
}

export interface ScientificUiSliderNode extends ScientificUiNodeBase {
  type: "Slider";
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  unit?: string;
}

export interface ScientificUiRangeSliderNode extends ScientificUiNodeBase {
  type: "RangeSlider";
  label: string;
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  unit?: string;
}

export interface ScientificUiOption {
  id: string;
  label: string;
  value?: ScientificUiValue;
}

export interface ScientificUiSelectNode extends ScientificUiNodeBase {
  type: "Select";
  label: string;
  options: ScientificUiOption[];
  value: string;
}

export interface ScientificUiCheckBoxNode extends ScientificUiNodeBase {
  type: "CheckBox";
  label: string;
  checked: boolean;
}

export interface ScientificUiButtonNode extends ScientificUiNodeBase {
  type: "Button";
  label: string;
  actionId?: string;
  payload?: ScientificUiValue;
  variant?: "primary" | "quiet";
}

export interface ScientificUiChartNode extends ScientificUiNodeBase {
  type: "VegaLiteChart";
  title?: string;
  mark: "point" | "line";
  x: { field: string; label?: string };
  y: { field: string; label?: string };
  rows?: ScientificUiRow[];
  binding?: string;
  dataRef?: string;
}

export interface ScientificUiPlotlyVolcanoChartNode
  extends ScientificUiNodeBase {
  type: "PlotlyVolcanoChart";
  title?: string;
  log2FoldChange: { field: string; label?: string };
  adjustedPValue: { field: string; label?: string };
  fdrThreshold: number;
  foldChangeThreshold: number;
  binding?: string;
  dataRef?: string;
}

export interface ScientificUiArtifactViewerNode extends ScientificUiNodeBase {
  type: "ArtifactViewer";
  artifactRef: string;
  title?: string;
}

export type ScientificUiNode =
  | ScientificUiContainerNode
  | ScientificUiTabsNode
  | ScientificUiTextNode
  | ScientificUiMarkdownNode
  | ScientificUiMetricNode
  | ScientificUiDataTableNode
  | ScientificUiSliderNode
  | ScientificUiRangeSliderNode
  | ScientificUiSelectNode
  | ScientificUiCheckBoxNode
  | ScientificUiButtonNode
  | ScientificUiChartNode
  | ScientificUiPlotlyVolcanoChartNode
  | ScientificUiArtifactViewerNode;

export interface ScientificUiCatalog {
  version: 1;
  root: ScientificUiNode;
}

export interface ScientificUiBindings {
  artifact_refs: string[];
  [key: string]: unknown;
}

export interface ScientificUiArtifactDocument {
  cadence_artifact_version: 1;
  version: 1;
  renderer: "scientific-ui";
  id: string;
  revision: number;
  title?: string;
  root: string;
  bindings?: ScientificUiBindings;
  catalog?: ScientificUiCatalog;
}

export interface CustomAppArtifactDocument {
  cadence_artifact_version: 1;
  version: 1;
  renderer: "custom-app";
  id: string;
  revision: number;
  title?: string;
  srcdoc: string;
  entrypoint: string;
  sandbox: {
    allow: ["scripts"];
    allow_same_origin: false;
    allow_top_navigation: false;
  };
  bindings?: ScientificUiBindings;
}

export interface UnsupportedArtifactRendererDocument {
  cadence_artifact_version?: number;
  version?: number;
  renderer: string;
  id?: string;
  revision?: number;
  [key: string]: unknown;
}

export type ArtifactRendererDocument =
  | ScientificUiArtifactDocument
  | CustomAppArtifactDocument
  | UnsupportedArtifactRendererDocument;

export type CadencePublishedArtifactFormat =
  | "document"
  | "table"
  | "figure"
  | "html"
  | "code"
  | "decision";

export type CadencePublishedArtifactDeliveryState =
  | "final"
  | "draft"
  | "blocked";

/**
 * Server-attested publish_artifact metadata. This format is never derived
 * from the Artifact body, MIME type, or filename in the browser.
 */
export interface CadencePublishedArtifactPresentation {
  format: CadencePublishedArtifactFormat;
  /** Delivery completeness, deliberately separate from scientific verification. */
  deliveryState?: CadencePublishedArtifactDeliveryState;
  title?: string;
  summary?: string;
  template?: string;
  citations?: string[];
  confidence?: string | number;
  supersedesArtifactId?: string;
  caption?: string;
  method?: string;
  dataSource?: string;
}

export interface CadencePublishedTableContent {
  format: "table";
  columns: string[];
  rows: Array<Record<string, string | number | boolean | null>>;
  notes?: string;
}

export interface CadencePublishedCodeContent {
  format: "code";
  files: Array<{ path: string; text: string }>;
  howToRun: string;
  outputs: string[];
}

export interface CadencePublishedDecisionContent {
  format: "decision";
  question: string;
  recommendation: "go" | "no_go" | "conditional_go" | "need_more_data";
  criteria: Array<{ name: string; met: boolean; note?: string }>;
  basedOnArtifactIds: string[];
  risks: string[];
  nextActions: string[];
}

export interface CadencePublishedHtmlContent {
  format: "html";
  /** Server-attested self-contained offline HTML. Preserve bytes for srcDoc. */
  html: string;
}

export type CadencePublishedArtifactContent =
  | CadencePublishedTableContent
  | CadencePublishedCodeContent
  | CadencePublishedDecisionContent
  | CadencePublishedHtmlContent;

export interface CadenceArtifact {
  id: string;
  path?: string;
  kind?: string;
  mimeType?: string;
  name: string;
  type:
    | "Notebook"
    | "CSV"
    | "Image"
    | "Structure"
    | "HTML"
    | "Docx"
    | "Report"
    | "Data";
  description?: string;
  updatedAt?: string;
  size?: string;
  status?: string;
  owner?: string | null;
  executionId?: string;
  /** Representation declared by the producing artifact provenance. */
  representation?: string;
  /** Coordinate source declared by the producing artifact provenance. */
  coordinateSource?: string;
  /** Server-calculated digest for anchored report selections. */
  contentSha256?: string;
  /** Server-calculated digest for a PDB/CIF coordinate Artifact. */
  coordinateSha256?: string;
  sourceRevision?: number;
  descriptorRevision?: number;
  previewUrl?: string;
  downloadUrl?: string;
  versions?: ArtifactVersion[];
  provenance?: ArtifactProvenance[];
  publishedPresentation?: CadencePublishedArtifactPresentation;
  publishedContent?: CadencePublishedArtifactContent;
  preview?: {
    kind:
      | "markdown"
      | "text"
      | "table"
      | "image"
      | "structure"
      | "html"
      | "code"
      | "decision";
    content: string;
    format?: CadenceStructureFormat;
  };
  scientificDocument?: ScientificArtifactDocument;
  /** A renderable local snapshot without a canonical revision. */
  localScientificDocument?: LocalScientificArtifactDocument;
  rendererDocument?: ArtifactRendererDocument;
}

export interface CadenceArtifactInquiryRequest {
  artifact: CadenceArtifact;
  revision?: number;
  selection?: ScientificSelection;
  intent: "query" | "change_proposal";
  input: string;
}

export interface CadenceArtifactOpenRequest {
  artifactId: string;
  requestId: number;
}

export type CadenceArtifactDetailState = "idle" | "loading" | "ready" | "error";

export type CadenceAttachmentInspectionStatus =
  | "stored"
  | "preparing"
  | "derived_available"
  | "unsupported"
  | "unavailable"
  | "failed"
  | "integrity_mismatch";

export interface CadenceAttachmentInspection {
  version?: number;
  status: CadenceAttachmentInspectionStatus;
  kind?: string;
  sourceSha256?: string;
  parser?: Record<string, unknown>;
  coverage?: Record<string, unknown>;
  reason?: { code: string; message: string };
}

export interface CadenceAttachment {
  id: string;
  name: string;
  size?: number;
  mimeType?: string;
  kind?: "upload" | "workspace";
  path?: string;
  projectId?: string;
  sessionId?: string;
  workspacePath?: string;
  inspection?: CadenceAttachmentInspection;
  documentSelection?: UploadDocumentSelection;
}

export type CadenceBTWResponse =
  | {
      status: "loading";
      agentId: string;
      requestId: string;
    }
  | {
      status: "completed";
      agentId: string;
      requestId: string;
      text: string;
      source: "agent";
    }
  | {
      status: "error";
      agentId: string;
      requestId: string;
      error: string;
    };

export interface CadenceResidue {
  position: number;
  aminoAcid: string;
  selected?: boolean;
  note?: string;
}

export interface CadenceContext {
  projectId: string;
  sessionId: string;
  workspacePath?: string;
  tab: CadenceTab;
  agentId?: string;
  artifactId?: string;
  artifactPath?: string;
  artifactRevision?: number;
  artifactExecutionId?: string;
  presentationArtifactRefs: string[];
  scientificSelection?: ScientificSelection;
  stableResidues: CadenceStableResidue[];
  residuePositions: number[];
}

export interface CadenceBioAdapter {
  mode?: CadenceMode;
  projects?: CadenceProject[];
  activeProjectId?: string;
  agents?: CadenceAgent[];
  agentEvents?: CadenceAgentEvent[];
  liveResearchActive?: boolean;
  researchStartedAt?: number;
  sessionRunning?: boolean;
  projectRuntimeReady?: boolean;
  streamState?: CadenceStreamState;
  collaboration?: CadenceCollaborationState;
  turns?: CadenceTurn[];
  evidence?: CadenceEvidence[];
  warnings?: string[];
  permissions?: CadencePermissionRequest[];
  activeTurnId?: string;
  permissionError?: string;
  artifacts?: CadenceArtifact[];
  composerCapabilities?: CadenceComposerCapability[];
  /** Durable Project uploads available to `@`; separate from the current turn selection. */
  availableAttachments?: CadenceAttachment[];
  residues?: CadenceResidue[];
  activeTab?: CadenceTab;
  /** null means an explicitly cleared controlled selection; omit for uncontrolled. */
  selectedAgentId?: string | null;
  selectedArtifactId?: string;
  openArtifactIds?: string[];
  besideArtifactId?: string;
  artifactOpenRequest?: CadenceArtifactOpenRequest;
  selectedStableResidues?: CadenceStableResidue[];
  selectedResiduePositions?: number[];
  projectSidebarCollapsed?: boolean;
  agentTeamCollapsed?: boolean;
  agentTeamWidth?: number;
  onProjectSelect?(projectId: string): void;
  onTabChange?(tab: CadenceTab): void;
  onAgentSelect?(agentId: string | null): void;
  onArtifactOpen?(artifactId: string, mode: ArtifactOpenMode): void;
  onArtifactClose?(artifactId: string): void;
  onArtifactDetailRequest?(artifactId: string): void;
  artifactDetailStates?: Record<string, CadenceArtifactDetailState>;
  artifactDetailErrors?: Record<string, string>;
  onArtifactDetailRetry?(artifactId: string): void;
  onResidueSelect?(position: number): void;
  onStableResidueSelectionChange?(residues: CadenceStableResidue[]): void;
  onResidueSelectionChange?(positions: number[]): void;
  onProjectSidebarCollapse?(collapsed: boolean): void;
  onOpenArtifactIdsChange?(artifactIds: string[]): void;
  onBesideArtifactChange?(artifactId?: string): void;
  onAgentTeamCollapse?(collapsed: boolean): void;
  onAgentTeamWidthChange?(width: number): void;
  onSendPrompt?(
    input: string,
    context: CadenceContext,
    attachments: CadenceAttachment[],
    intent: CadenceComposerIntent,
    references: CadenceComposerReference[],
  ): Promise<void> | void;
  /**
   * Appends plain-language direction to the active Organization Lead turn.
   * It is intentionally narrower than `onSendPrompt`: no new ProjectTurn,
   * attachments, Artifact scope, or browser-selected Agent can cross here.
   */
  onSteerPrompt?(parentRequestId: string, input: string): Promise<void> | void;
  onSendBTW?(
    agentId: string,
    input: string,
    context: CadenceContext,
  ): Promise<void> | void;
  onArtifactComposerSubmit?(
    request: CadenceArtifactInquiryRequest,
  ): Promise<string | void> | string | void;
  attachments?: CadenceAttachment[];
  uploading?: boolean;
  uploadError?: string;
  onUploadFiles?(files: File[]): void;
  onAttachProjectInput?(attachmentId: string): void;
  onRemoveAttachment?(attachmentId: string): void;
  btwResponse?: CadenceBTWResponse;
  onBTWResponseDismiss?(requestId: string): void;
  projectListState?: "idle" | "loading" | "ready" | "error";
  projectListError?: string;
  projectContentState?: "idle" | "loading" | "ready" | "error";
  projectSnapshotError?: string;
  onRetryProjects?(): void;
  onRetryProject?(): void;
  creatingProject?: boolean;
  createProjectError?: string;
  projectNotice?: string;
  onProjectNoticeDismiss?(): void;
  onCreateProject?(input: {
    name: string;
    question?: string;
  }): Promise<void> | void;
  /** Available only when this loopback Cadence server can open a native picker. */
  localFolderImportAvailable?: boolean;
  onSelectLocalFolder?(): Promise<
    | { selectionId: string; name: string; expiresAt?: number }
    | undefined
  >;
  onImportProject?(input: {
    selectionId: string;
    mode: "copy" | "in_place";
    name?: string;
  }): Promise<void> | void;
  projectOwnerDisplayName?: string;
  userError?: string;
  onboardingActive?: boolean;
  onboardingStep?: number;
  onOnboardingStepChange?(stepIndex: number): void;
  onOnboardingDismiss?(): void;
  onOnboardingUnavailable?(): void;
  onOnboardingComplete?(): void;
  onOnboardingReplay?(): void;
  requestError?: string;
  organizationError?: string;
  onRetryOrganization?(): void;
  onPermissionDecision?(
    requestId: string,
    decision: "approve" | "deny",
  ): Promise<void> | void;
  onCancelTurn?(turnId: string): Promise<void> | void;
  turnsLoading?: boolean;
  turnsLoadingOlder?: boolean;
  turnsHasMore?: boolean;
  turnsError?: string;
  onRetryTurns?(): void;
  turnsRetryLabel?: string;
  onLoadOlderTurns?(): Promise<void> | void;
  scientificOperation?: ScientificOperationResult;
  onRetryUpload?(): void;
  onScientificArtifactApply?(
    artifactId: string,
    baseRevision: number,
    document: ScientificArtifactDocument,
  ): Promise<ScientificArtifactDocument>;
  onReportTextReplacementApply?(
    proposal: ReportTextReplacementProposal,
  ): Promise<{ content: string; contentSha256: string }>;
  researchStateViews?: Partial<
    Record<ResearchStateSection, ResearchStateView>
  >;
  researchStateLoadingSections?: ResearchStateSection[];
  researchStateErrors?: Partial<Record<ResearchStateSection, string>>;
  onResearchStateOpen?(): void;
  onResearchStateSectionLoad?(section: ResearchStateSection): void;
  onResearchStateRefresh?(): void;
  onResearchStateArtifactOpen?(reference: string): void;
}

export interface CadenceBioAppProps extends CadenceBioAdapter {
  className?: string;
  /** Prevent mutations when the surface is an illustrative projection. */
  readOnly?: boolean;
  /** Keep the request composer inert until a project can receive the request. */
  composerDisabled?: boolean;
  composerDisabledReason?: string;
}
